import { computed, makeObservable } from 'mobx'
import type { MobxDocument } from '../firestore-mobx'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import {
  empty,
  type FirestoreSectionAssignment,
} from '../firestore/SectionAssignment'
import { buildSlideDeckObservableModelDocument } from '../firestore/SlideDeck'
import type { FirebaseRepository } from './FirebaseRepository'
import { DateTime } from 'luxon'
import {
  AssignmentState,
  AssignmentStatus,
  AssignmentStatusOld,
} from '../types'
import {
  GROUPING_SIZE_MAXIMUM_DEFAULT,
  GROUPING_SIZE_MINIMUM_DEFAULT,
} from '../constants'
import { getAssignmentStatus } from '../sources-of-truth/assignmentStatus'
import { dateTime } from '../util'

export type ScalarsType = FirestoreSectionAssignment['assignmentGradingScalars']

export class SectionAssignment extends ObservableModelWithDecorators<FirestoreSectionAssignment> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSectionAssignment>
  ) {
    super(repository, doc)

    makeObservable(this)
  }

  @computed
  get sectionId() {
    return this.unsafeData?.sectionId ?? ''
  }

  @computed
  get expiresAt() {
    return this.unsafeData?.expiresAt ?? undefined
  }

  @computed
  get assignedAt() {
    return this.unsafeData?.assignedAt ?? undefined
  }

  @computed
  get assignmentOverdueAt() {
    return this.unsafeData?.assignmentOverdueAt ?? undefined
  }

  /** If the assignment allows late submissions, this field is filled with expiresAt. */
  @computed
  get extendedDueDate() {
    return this.assignmentOverdueAt ? this.expiresAt : undefined
  }

  /** The date students are told to submit their assignments by. expiresAt may be later if late submissions are allowed. */
  @computed
  get dueDate() {
    const hasLateSubmissions = !!this.assignmentOverdueAt

    if (hasLateSubmissions && this.assignmentOverdueAt) {
      return this.assignmentOverdueAt
    } else if (this.expiresAt) {
      return this.expiresAt
    } else {
      return undefined
    }
  }

  @computed
  get dueDateTime() {
    return dateTime(this.dueDate)
  }

  @computed
  get individualDeadlines() {
    return this.unsafeData?.individualDeadlines ?? {}
  }

  @computed
  get hasIndividualDeadlines() {
    return Object.keys(this.individualDeadlines).length > 0
  }

  /** Per-student date overrides (assignedAt/overdueAt/expiresAt), if any. */
  getIndividualDeadline(userId: string) {
    return this.individualDeadlines[userId]
  }

  /**
   * The student's own hard end: the later of their individual overdueAt and
   * expiresAt. Undefined when the student has no override with an end date.
   * This is the enforcement anchor (max) — used for lockout, room liveness and
   * late-completion checks. Manually-granted extensions store a single date
   * (overdueAt only), so end == due == cutoff for them; Canvas-synced entries
   * carry a distinct soft due (overdueAt) and hard cutoff (expiresAt), in which
   * case this returns the cutoff. See getEffectiveDueDate / getEffectiveExpiresAt.
   */
  getIndividualDeadlineEnd(userId: string): Date | undefined {
    const entry = this.getIndividualDeadline(userId)
    if (!entry) return undefined
    const { overdueAt, expiresAt } = entry
    if (overdueAt && expiresAt) {
      return overdueAt > expiresAt ? overdueAt : expiresAt
    }
    return overdueAt ?? expiresAt
  }

  /**
   * The hard cutoff (enforcement) for a specific student. An individual
   * deadline is authoritative for that student: their entry replaces the
   * assignment window entirely (they trade the late-submission window for their
   * own deadline). Resolves to the entry's end — max(overdueAt, expiresAt), the
   * same anchor getIndividualDeadlineEnd and the force-grade cron use — so a
   * Canvas entry whose best-effort expiresAt landed before the student's
   * extended due date never locks them out ahead of the date the UI shows.
   * A manually-granted extension (overdueAt only) resolves to that one date,
   * unchanged. Everyone else follows the assignment expiresAt. The extend-only
   * intent is enforced by the writers, not here.
   */
  getEffectiveExpiresAt(userId: string): Date | undefined {
    return this.getIndividualDeadlineEnd(userId) ?? this.expiresAt
  }

  /**
   * The due date shown to a specific student (soft — display / "is it
   * overdue", never enforcement). Prefers the entry's overdueAt (the Canvas
   * due date), falling back to expiresAt when only a single date was stored.
   * For a manual extension (overdueAt only) this equals getEffectiveExpiresAt,
   * so nothing changes; for a Canvas entry with both dates the due date is the
   * earlier overdueAt while the cutoff stays the later expiresAt. Everyone else
   * sees the assignment-level dueDate.
   */
  getEffectiveDueDate(userId: string): Date | undefined {
    const entry = this.getIndividualDeadline(userId)
    return entry?.overdueAt ?? entry?.expiresAt ?? this.dueDate
  }

  /** When the assignment opens for a specific student. */
  getEffectiveAssignedAt(userId: string): Date | undefined {
    return this.getIndividualDeadline(userId)?.assignedAt ?? this.assignedAt
  }

  isExpiredForUser(userId: string): boolean {
    const effectiveExpiresAt = this.getEffectiveExpiresAt(userId)
    return effectiveExpiresAt
      ? effectiveExpiresAt.getTime() < this.repository.currentMinute.toMillis()
      : false
  }

  /**
   * True when the user has an individual deadline whose end is still in the
   * future. Used to let an extended student keep access past section
   * completion (their window legitimately outlives the section).
   */
  hasFutureIndividualDeadline(userId: string): boolean {
    const end = this.getIndividualDeadlineEnd(userId)
    return end
      ? end.getTime() > this.repository.currentMinute.toMillis()
      : false
  }

  /**
   * The latest effective hard cutoff across a set of users (e.g. a room's
   * members). A room stays live until its last member's deadline passes, so
   * instructor- and room-level views must use this MAX rather than the
   * assignment-level expiresAt — otherwise a room containing an extended
   * student is mislabeled Expired/Abandoned while that student still has time.
   * Returns undefined when the assignment has no cutoff (individual deadlines
   * never apply without one). Falls back to assignment expiresAt for members
   * with no override (or when the set is empty).
   */
  getEffectiveExpiresAtForUsers(userIds: string[]): Date | undefined {
    if (!this.expiresAt) return undefined
    let latest = this.expiresAt
    for (const userId of userIds) {
      const end = this.getEffectiveExpiresAt(userId)
      if (end && end.getTime() > latest.getTime()) latest = end
    }
    return latest
  }

  /**
   * True when any of the given users has an individual deadline still in the
   * future. Used as the section-completion suppression flag for room-level
   * views: a room keeps living past section completion while any member's
   * window legitimately outlives the section.
   */
  hasFutureIndividualDeadlineForAnyUser(userIds: string[]): boolean {
    return userIds.some((userId) => this.hasFutureIndividualDeadline(userId))
  }

  /** Like assignedAtIsInFuture, but honoring a student's individual availableFrom. */
  assignedAtIsInFutureForUser(userId: string): boolean {
    const assignedAt = this.getEffectiveAssignedAt(userId)
    return assignedAt
      ? assignedAt.getTime() > this.repository.currentMinute.toMillis()
      : false
  }

  groupCompletionWasLate = ({
    roomStartedAt,
  }: {
    roomStartedAt: Date | undefined
  }) => {
    if (!roomStartedAt) return false

    if (!this.assignmentOverdueAt) return false

    return roomStartedAt.getTime() > this.assignmentOverdueAt.getTime()
  }

  /**
   * Like groupCompletionWasLate, but for a specific student. A student with an
   * individual deadline is late only if the room started after their own
   * deadline end — individual deadlines have no late-submission window.
   */
  completionWasLateForUser = ({
    userId,
    roomStartedAt,
  }: {
    userId: string
    roomStartedAt: Date | undefined
  }) => {
    if (!roomStartedAt) return false

    const individualEnd = this.getIndividualDeadlineEnd(userId)
    if (individualEnd) {
      return roomStartedAt.getTime() > individualEnd.getTime()
    }

    return this.groupCompletionWasLate({ roomStartedAt })
  }

  @computed
  get assignmentStatus(): AssignmentStatus {
    return getAssignmentStatus(this.data)
  }

  @computed
  get hasRubricWeight(): boolean {
    return (this.data.assignmentGradingScalars?.totalRubricScore ?? 0) > 0
  }

  // TODO: Remove when newInstructorPages flag is removed
  @computed
  get assignmentStatusOld(): AssignmentStatusOld {
    // milliseconds in a day
    const oneDay = 1000 * 60 * 60 * 24
    if (this.expiresAt && this.expiresAt.getTime() + oneDay > Date.now()) {
      return AssignmentStatusOld.pending
    }
    return AssignmentStatusOld.completed
  }

  @computed
  get expiresAtDate() {
    return this.expiresAt ? DateTime.fromJSDate(this.expiresAt) : undefined
  }

  @computed
  get assignedAtDate() {
    return this.assignedAt ? DateTime.fromJSDate(this.assignedAt) : undefined
  }

  @computed
  get assignedAtIsInFuture() {
    return this.assignedAt
      ? this.assignedAt.getTime() > this.repository.currentMinute.toMillis()
      : false
  }

  @computed
  get expiresAtIsInPast() {
    return this.expiresAt ? this.expiresAt.getTime() < Date.now() : false
  }

  @computed
  get isCancelled() {
    return this.data.assignmentState === AssignmentState.canceled
  }

  @computed
  get isDraft() {
    return this.data.assignmentState === AssignmentState.draft
  }

  @computed
  get isUnconfigured() {
    return this.data.assignmentState === AssignmentState.unconfigured
  }

  @computed
  get isActive() {
    return this.data.assignmentState === AssignmentState.active
  }

  @computed
  get formattedAssignedAt() {
    // EX. Oct 18, 2024
    return this.assignedAt
      ? DateTime.fromJSDate(this.assignedAt).toFormat('LLL d, yyyy')
      : ''
  }

  @computed
  get formattedAssignedAtWithTime() {
    // EX. Oct 18, 2024 12:00 PM
    return this.assignedAt
      ? DateTime.fromJSDate(this.assignedAt).toFormat('LLL d, yyyy h:mm a')
      : ''
  }

  @computed
  get formattedExpiresAt() {
    return this.expiresAt
      ? DateTime.fromJSDate(this.expiresAt).toFormat('LLL d, yyyy')
      : ''
  }

  @computed
  get formattedExpiresAtWithTime() {
    return this.expiresAt
      ? DateTime.fromJSDate(this.expiresAt).toFormat('LLL d, yyyy h:mm a')
      : ''
  }

  @computed
  get formattedDueDate() {
    return this.dueDate
      ? DateTime.fromJSDate(this.dueDate).toFormat('LLL d, yyyy')
      : ''
  }

  @computed
  get formattedDueDateWithTime() {
    return this.dueDate
      ? DateTime.fromJSDate(this.dueDate).toFormat('LLL d, yyyy h:mm a')
      : ''
  }

  @computed
  get isExpired() {
    return this.expiresAt
      ? this.expiresAt.getTime() < this.repository.currentMinute.toMillis()
      : false
  }

  /**
   * Whether the given user has any individual date override. Based on entry
   * existence (not just an end date) so an availableFrom-only override — which
   * getEffectiveAssignedAt honors — still reads as extended in the UI.
   */
  hasIndividualDeadline(userId: string): boolean {
    return this.getIndividualDeadline(userId) !== undefined
  }

  formattedEffectiveAssignedAt(userId: string): string {
    const date = this.getEffectiveAssignedAt(userId)
    return date ? DateTime.fromJSDate(date).toFormat('LLL d, yyyy') : ''
  }

  formattedEffectiveAssignedAtWithTime(userId: string): string {
    const date = this.getEffectiveAssignedAt(userId)
    return date ? DateTime.fromJSDate(date).toFormat('LLL d, yyyy h:mm a') : ''
  }

  formattedEffectiveDueDate(userId: string): string {
    const date = this.getEffectiveDueDate(userId)
    return date ? DateTime.fromJSDate(date).toFormat('LLL d, yyyy') : ''
  }

  formattedEffectiveDueDateWithTime(userId: string): string {
    const date = this.getEffectiveDueDate(userId)
    return date ? DateTime.fromJSDate(date).toFormat('LLL d, yyyy h:mm a') : ''
  }

  formattedEffectiveExpiresAt(userId: string): string {
    const date = this.getEffectiveExpiresAt(userId)
    return date ? DateTime.fromJSDate(date).toFormat('LLL d, yyyy') : ''
  }

  formattedEffectiveExpiresAtWithTime(userId: string): string {
    const date = this.getEffectiveExpiresAt(userId)
    return date ? DateTime.fromJSDate(date).toFormat('LLL d, yyyy h:mm a') : ''
  }

  @computed
  get slideDeck() {
    return buildSlideDeckObservableModelDocument(
      this.repository,
      this.data?.slideDeckId
    )?.model
  }

  @computed
  get assignmentType(): AssignmentType | undefined {
    if (!this.hasData) return undefined
    // return index of the rubric type enum
    switch (this.data.assignmentType) {
      case 0:
        return AssignmentType.ManyGroups
      case 1:
        return AssignmentType.OneGroup
      default:
        return undefined
    }
  }

  @computed
  get groupingType(): AssignmentGroupingType | undefined {
    if (!this.hasData) return undefined
    // return index of the rubric type enum
    switch (this.data.groupingType) {
      case 0:
        return AssignmentGroupingType.manual
      case 1:
        return AssignmentGroupingType.automaticRandom
      default:
        return undefined
    }
  }

  @computed
  get hasGrading() {
    return Boolean(this.unsafeData?.assignmentGradingScalars)
  }

  @computed
  /** only used for brightspace as of 11/13/25 */
  get ltiAssignmentNeedsGradingLineItemId() {
    // if assignment has passed config step, no longer validate grading items
    if (!this.isUnconfigured) return false

    // if there are multiple options and we have not selected a valid one
    // we need to prompt user for selection
    if (
      this.data.ltiGradingLineItemOptions &&
      this.data.ltiGradingLineItemOptions.length > 1 &&
      (!this.data.ltiGradingLineItemId ||
        !this.data.ltiGradingLineItemOptions.some(
          (option) => option.id === this.data.ltiGradingLineItemId
        ))
    )
      return true

    return false
  }

  @computed
  get maxGroupSize() {
    return this.data?.groupingSizeMaximum ?? GROUPING_SIZE_MAXIMUM_DEFAULT
  }

  @computed
  get isSolo() {
    return this.maxGroupSize === 1
  }

  @computed
  get minGroupSize() {
    return this.data?.groupingSizeMinimum ?? GROUPING_SIZE_MINIMUM_DEFAULT
  }

  @computed
  get desiredGroupSize() {
    return this.data?.groupingSize ?? this.maxGroupSize
  }

  @computed
  get forbidStudentGroupJoining(): boolean {
    return (
      this.data.forbidStudentGroupJoining ??
      this.data.forbidStudentGroupSelection ??
      false
    )
  }

  @computed
  get forbidStudentGroupLeaving(): boolean {
    return (
      this.data.forbidStudentGroupLeaving ??
      this.data.forbidStudentGroupSelection ??
      false
    )
  }

  @computed
  get minScheduleDate(): DateTime {
    const now = this.repository.currentMinute
    return this.assignedAtDate
      ? DateTime.max(now, this.assignedAtDate.startOf('minute'))
      : now
  }
}

export enum AssignmentType {
  ManyGroups, // Used to be called studentLed
  OneGroup, // Used to be called instructorLed
}

/// An enum that represents the type of assignment group.
export enum AssignmentGroupingType {
  /// manual
  manual,

  /// automaticRandom
  automaticRandom,
}

// Re-exported for the web import path (@breakoutlearning/firebase-repository/models/SectionAssignment).
// Single source of truth is firestore/SectionAssignment/types.
export { CompletionMetric } from '../firestore/SectionAssignment/types'
